import clsx from "clsx";

import { DashboardBackdrop } from "./dashboard-backdrop";

import type { Field as FunnelField } from "./funnel";

// While the funnel finds the lead's run (or opens a session), there is nothing
// worth reading, so the screen shows none: just the ground the page was first
// painted on (`.boot-surface`, inline in the application layout). A label here
// only flashed for a moment between first paint and the first slide.
export function LoadingScreen() {
  return <div className="boot-surface fixed inset-0" aria-busy="true" />;
}

// The chrome every redesigned slide shares: one card, centred over the blurred
// dashboard it leads to, with the question as its heading. The funnel is being redesigned a slide at a
// time and the slides that have landed all take this shape, so it lives here
// rather than being copied into each of them.
//
// `my-auto` rather than `items-center` on purpose — a flex-centred child that
// outgrows the viewport gets clipped at the top, and the card is tall enough on
// a phone for that to be a real screen rather than a hypothetical one.
export function FunnelScreen({
  title,
  subtitle,
  children,
  above,
  below,
  wide,
  back,
}: {
  title: string;
  // One line under the question. Earns its place where the answer shape is not
  // obvious from looking — that more than one option can be picked, say.
  subtitle?: string | null;
  children: React.ReactNode;
  // Above the question, inside the card. So far: the faces on the greeting.
  above?: React.ReactNode;
  below?: React.ReactNode;
  // For the screens whose answers are cards several across rather than a
  // column of fields.
  wide?: boolean;
  // Where the previous question was. Absent on the first screen, and on every
  // screen after the account exists — from there the funnel only goes forward.
  back?: () => void;
}) {
  return (
    <div className="isolate flex min-h-screen flex-col px-4 py-10">
      <DashboardBackdrop />
      <div
        className={clsx(
          "mx-auto my-auto w-full",
          wide ? "max-w-[880px]" : "max-w-[460px]"
        )}
      >
        <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-xl sm:p-8">
          {back && (
            <button
              type="button"
              onClick={back}
              className="mb-4 text-caption font-normal text-zinc-500 transition-colors hover:text-zinc-700"
            >
              ← Back
            </button>
          )}
          {above}
          <h1 className="text-display-sm font-semibold text-zinc-900">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-2 text-body text-zinc-500">{subtitle}</p>
          )}
          {children}
        </div>
        {below}
      </div>
    </div>
  );
}

// The funnel's one button. Filled is the commitment on a screen; the choice
// cards above it carry the rest of the weight.
export function PrimaryButton({
  children,
  disabled,
  type = "button",
  onClick,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  type?: "button" | "submit";
  onClick?: () => void;
}) {
  return (
    <button
      type={type === "submit" ? "submit" : "button"}
      disabled={disabled}
      onClick={onClick}
      className="h-11 w-full rounded-lg bg-blue-600 text-body font-semibold text-white transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 active:bg-blue-800 disabled:opacity-50"
    >
      {children}
    </button>
  );
}

// One answer, as a card. `selected` is only ever true on the multi-pick screen —
// where a single pick advances immediately, there is nothing to show selected.
export function ChoiceCard({
  label,
  selected,
  illustration,
  className,
  onClick,
}: {
  label: string;
  selected?: boolean;
  illustration?: string;
  className?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={clsx(
        "flex flex-col rounded-xl border p-3 text-left text-body transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2",
        className,
        selected
          ? "border-blue-600 bg-blue-50 text-zinc-900 ring-1 ring-blue-600"
          : "border-zinc-300 bg-white text-zinc-700 hover:border-zinc-400 hover:bg-zinc-50"
      )}
    >
      {illustration && (
        <img
          src={illustration}
          alt=""
          className="mb-3 block aspect-[4/3] w-full rounded-lg object-cover"
        />
      )}
      <span className="font-medium">{label}</span>
    </button>
  );
}

// One typed answer. `hideLabel` is for the screens where the question above the
// field already names it and a second label would just repeat it — the label
// still ships, for anyone listening rather than looking.
export function Field({
  field,
  value,
  error,
  hideLabel,
  onChange,
}: {
  field: FunnelField;
  value: string;
  error?: string;
  hideLabel?: boolean;
  onChange: (value: string) => void;
}) {
  const errorId = `${field.key}-error`;

  return (
    <div>
      <label
        htmlFor={field.key}
        className={clsx(
          hideLabel ? "sr-only" : "block text-caption text-zinc-700"
        )}
      >
        {field.label}
      </label>
      <input
        id={field.key}
        name={field.key}
        type={field.inputType === "email" ? "email" : "text"}
        value={value}
        placeholder={field.placeholder ?? undefined}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={clsx(
          "h-11 w-full rounded-lg border px-3 text-body text-zinc-900 transition-colors placeholder:text-zinc-400 focus:outline-none focus:ring-2",
          hideLabel || "mt-1.5",
          error
            ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
            : "border-zinc-300 focus:border-blue-600 focus:ring-blue-600/20"
        )}
      />
      {error && (
        <p
          id={errorId}
          className="mt-1.5 text-caption font-normal text-red-600"
        >
          {error}
        </p>
      )}
    </div>
  );
}
